import React, { useEffect, useState } from "react";
import Sidebar from "./Sidebar";
import { FaBarsStaggered } from "react-icons/fa6";
import axios from "axios";
import { IoSearchOutline } from "react-icons/io5";
import Loader from "../../components/Loader";
import AdminNav from "./AdminNav";
import Swal from "sweetalert2";
import { Modal, Input, Button, Form, Space, Table, Select } from "antd";
import { toast } from "react-toastify";
import { Link, useNavigate } from "react-router-dom";
import { formatNumber } from "../../utils/formateNum";

const Fund = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [funds, setFunds] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredFunds, setFilteredFunds] = useState([]);
  const [isNavModalVisible, setIsNavModalVisible] = useState(false);
  const [isEnrollModalVisible, setIsEnrollModalVisible] = useState(false);
  const [selectedFund, setSelectedFund] = useState(null);
  const [navData, setNavData] = useState([]);
  const [enrolledUsers, setEnrolledUsers] = useState([]);
  const [fundClasses, setFundClasses] = useState([]);
  const [users, setUsers] = useState([]);

  const [classFilter, setClassFilter] = useState(""); // filter for already-enrolled
  const navigate = useNavigate();
 const token = localStorage.getItem("admintoken");
  const [navForm] = Form.useForm();
  const [enrollForm] = Form.useForm();

  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);

  // Fetch all funds
  const fetchFunds = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_API}/api/v1/fund`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setFunds(data);
      setFilteredFunds(data);
    } catch (error) {
      console.log(error);
      toast.error("Failed to fetch funds");
    } finally {
      setLoading(false);
    }
  };

  // Fetch all users with KYC verified (kycstatus === 2)
  const fetchUsers = async () => {
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_API}/api/v1/auth/m-users`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUsers(data.users.filter((u) => u.kycstatus === 2));
    } catch (error) {
      console.log(error);
    }
  };

  // Fetch classes for a given fund
  const fetchFundClasses = async (fundId) => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_API}/api/v1/fund/classes/${fundId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setFundClasses(data.data);
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch fund classes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFunds();
    fetchUsers();
  }, []);

  // Filter funds list based on search term
  useEffect(() => {
    setFilteredFunds(
      searchTerm.trim()
        ? funds.filter(
            (f) =>
              f.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
              f.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
              f.type.toLowerCase().includes(searchTerm.toLowerCase())
          )
        : funds
    );
  }, [searchTerm, funds]);

  // Show “Add NAV” modal for given fund
  const showNavModal = (fund) => {
    setSelectedFund(fund);
    fetchNavData(fund._id);
    setIsNavModalVisible(true);
  };

  // Show “Enroll Users” modal for given fund
  const showEnrollModal = (fund) => {
    setSelectedFund(fund);
    fetchEnrolledUsers(fund._id);
    fetchFundClasses(fund._id);
    setClassFilter("");
    setIsEnrollModalVisible(true);
  };

  // Fetch existing NAV entries for a fund
  const fetchNavData = async (fundId) => {
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_API}/api/v1/fund/nav/${fundId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNavData(data);
    } catch (error) {
      console.log(error);
      toast.error("Failed to fetch NAV data");
    }
  };

  // Fetch already-enrolled users for a fund
  const fetchEnrolledUsers = async (fundId) => {
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_API}/api/v1/fund/enroll-user/${fundId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEnrolledUsers(data);
    } catch (error) {
      console.log(error);
      toast.error("Failed to fetch enrolled users");
    }
  };

  const handleNavCancel = () => {
    setIsNavModalVisible(false);
    navForm.resetFields();
  };
  const handleEnrollCancel = () => {
    setIsEnrollModalVisible(false);
    enrollForm.resetFields();
  };

  // Submit new NAV entry
  const handleNavSubmit = async (values) => {
    try {
      setLoading(true);
      const response = await axios.post(
        `${process.env.REACT_APP_API}/api/v1/fund/nav/${selectedFund._id}`,
        values,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        toast.success("NAV added successfully");
        fetchNavData(selectedFund._id);
        navForm.resetFields();
      }
    } catch (error) {
      console.log(error);
      toast.error(error.response?.data?.message || "Failed to add NAV");
    } finally {
      setLoading(false);
    }
  };

  // Submit new enrollment
  const handleEnrollSubmit = async (values) => {
    try {
      setLoading(true);
      const response = await axios.post(
        `${process.env.REACT_APP_API}/api/v1/fund/enroll-user/${selectedFund._id}`,
        values,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        toast.success("User enrolled successfully");
        fetchEnrolledUsers(selectedFund._id);
        enrollForm.resetFields();
        setClassFilter("");
      }
    } catch (error) {
      console.log(error);
      toast.error(error.response?.data?.message || "Failed to enroll user");
    } finally {
      setLoading(false);
    }
  };

  // Delete a fund
  const handleDelete = async (fundId) => {
    try {
      const result = await Swal.fire({
        title: "Are you sure?",
        text: "This will permanently delete the fund!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "Delete",
        cancelButtonText: "Cancel",
      });
      if (!result.isConfirmed) return;
      setLoading(true);
      const response = await axios.delete(
        `${process.env.REACT_APP_API}/api/v1/fund/${fundId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        toast.success("Fund deleted successfully");
        fetchFunds();
      }
    } catch (error) {
      console.log(error);
      toast.error(error.response?.data?.message || "Failed to delete fund");
    } finally {
      setLoading(false);
    }
  };

  // Table columns for Funds list
  const fundColumns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Code",
      dataIndex: "code",
      key: "code",
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
    },
    {
      title: "Launch Date",
      dataIndex: "launchDate",
      key: "launchDate",
      render: (date) => {
        const d = new Date(date);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space size="middle">
          <button
            className="clear-btn"
            onClick={() =>
              navigate(`/dashboard/admin/fund-details/${record._id}`)
            }
          >
            Details
          </button>
          <Button
            onClick={() =>
              navigate(`/dashboard/admin/add-class/${record._id}`, {
                state: { fundName: record.name },
              })
            }
          >
            Add Class
          </Button>
          <Button onClick={() => showNavModal(record)}>Add NAV</Button>
          <Button onClick={() => showEnrollModal(record)}>Enroll Users</Button>
          <Button danger onClick={() => handleDelete(record._id)}>
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  // Table columns for NAV entries
  const navColumns = [
    {
      title: "NAV Value",
      dataIndex: "NAV",
      key: "NAV",
      render: (nav) => formatNumber(nav),
    },
    {
      title: "Date",
      dataIndex: "createdAt",
      key: "date",
      render: (date) => {
        const d = new Date(date);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        const hours = String(d.getHours()).padStart(2, "0");
        const minutes = String(d.getMinutes()).padStart(2, "0");
        return `${year}-${month}-${day} ${hours}:${minutes}`;
      },
    },
  ];

  // Table columns for “already enrolled” users, now showing Fund and Class
  const enrolledUsersColumns = [
    {
      title: "Fund",
      dataIndex: "fund",
      key: "fund",
      render: () => selectedFund?.name || "—",
    },
    {
      title: "Class",
      dataIndex: "class",
      key: "class",
      render: (cls) => cls?.class || "—",
    },
    {
      title: "User",
      dataIndex: "user",
      key: "user",
      render: (user) => user?.name || "N/A",
    },
    {
      title: "Email",
      dataIndex: "user",
      key: "email",
      render: (user) => user?.email || "N/A",
    },
    {
      title: "Balance",
      dataIndex: "balance",
      key: "balance",
      render: (bal) => formatNumber(bal),
    },
    {
      title: "Deposit Balance",
      dataIndex: "depositBalance",
      key: "depositBalance",
      render: (dep) => formatNumber(dep),
    },
  ];

  // Filter enrolledUsers by selected class
  const filteredEnrolledUsers = classFilter
    ? enrolledUsers.filter((enr) => enr.class._id === classFilter)
    : enrolledUsers;

  return (
    <div className="wrapper d-flex align-items-stretch">
      {loading && <Loader />}
      <Sidebar sidebarOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
      <div id="content" className="px-2">
        <button
          type="button"
          id="openSidebar"
          onClick={toggleSidebar}
          className="bars-btn"
        >
          <FaBarsStaggered />
        </button>
        <AdminNav />

        {/* Search and Add buttons */}
        <div className="search-add-main">
          <div className="search-main mt-0">
            <input
              type="text"
              className="search-inp"
              placeholder="Search funds..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <div className="search-icon">
              <IoSearchOutline />
            </div>
          </div>
          <div>
            <Link
              to={"/dashboard/admin/fund-requests"}
              className="btn btn-primary"
            >
              Fund requests
            </Link>
            <Link
              to={"/dashboard/admin/add-fund"}
              className="btn btn-primary ms-3"
            >
              Add New Fund
            </Link>
          </div>
        </div>

        {/* Funds table */}
        <div className="tbl-main">
          <Table
            columns={fundColumns}
            dataSource={filteredFunds}
            rowKey="_id"
            pagination={{ pageSize: 10 }}
          />
        </div>
      </div>

      {/* Add NAV Modal */}
      <Modal
        title={`Add NAV for ${selectedFund?.name || ""}`}
        open={isNavModalVisible}
        onCancel={handleNavCancel}
        footer={null}
      >
        <Form form={navForm} onFinish={handleNavSubmit} layout="vertical">
          <Form.Item
            label="NAV Value"
            name="NAV"
            rules={[{ required: true, message: "Please enter NAV value" }]}
          >
            <Input type="number" step="0.01" min="0" />
          </Form.Item>

          <Table
            columns={navColumns}
            dataSource={navData}
            rowKey="_id"
            pagination={{ pageSize: 5 }}
          />

          <Form.Item style={{ marginTop: 20 }}>
            <Space>
              <Button type="primary" htmlType="submit">
                Add NAV
              </Button>
              <Button onClick={handleNavCancel}>Cancel</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Enroll User Modal */}
      <Modal
        title={`Enroll Users to ${selectedFund?.name || ""}`}
        open={isEnrollModalVisible}
        onCancel={handleEnrollCancel}
        footer={null}
        width={1200}
      >
        <Form form={enrollForm} onFinish={handleEnrollSubmit} layout="vertical">
          <div className="row">
            {/* User Dropdown */}
            <div className="col-md-6">
              <Form.Item
                label="User"
                name="user"
                rules={[{ required: true, message: "Please select user" }]}
              >
                <Select placeholder="Select user">
                  {users.map((u) => (
                    <Select.Option key={u._id} value={u._id}>
                      {u.name} ({u.email})
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </div>

            {/* Class Dropdown */}
            <div className="col-md-6">
              <Form.Item
                label="Class"
                name="class"
                rules={[{ required: true, message: "Please select class" }]}
              >
                <Select
                  placeholder={loading ? "Loading classes…" : "Select class"}
                  disabled={loading || fundClasses.length === 0}
                >
                  {fundClasses
                    .filter((cls) => {
                      const selectedUserId = enrollForm.getFieldValue("user");
                      if (!selectedUserId) return true;
                      // Prevent enrolling the same user/class pair twice
                      return !enrolledUsers.some(
                        (enr) =>
                          enr.user._id === selectedUserId &&
                          enr.class._id === cls._id
                      );
                    })
                    .map((cls) => (
                      <Select.Option key={cls._id} value={cls._id}>
                        {cls.class}
                      </Select.Option>
                    ))}
                </Select>
              </Form.Item>
            </div>

            {/* Initial Balance */}
            <div className="col-md-6">
              <Form.Item
                label="Initial Balance"
                name="balance"
                rules={[
                  { required: true, message: "Please enter initial balance" },
                ]}
              >
                <Input type="number" min="0" step="0.01" />
              </Form.Item>
            </div>
          </div>

          {/* Already Enrolled Table */}

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                Enroll User
              </Button>
            </Space>
          </Form.Item>

          {/* Filter by Class for “Already Enrolled” */}
          <div
            style={{
              marginBottom: 16,
              display: "flex",
              gap: 16,
              flexDirection: "column",
            }}
          >
            <label className="mb-0">Classes</label>
            <Select
              placeholder="Filter by class"
              className="w-50"
              value={classFilter}
              onChange={setClassFilter}
              allowClear
            >
              {fundClasses.map((cls) => (
                <Select.Option key={cls._id} value={cls._id}>
                  {cls.class}
                </Select.Option>
              ))}
            </Select>
          </div>
          <Table
            columns={enrolledUsersColumns}
            dataSource={filteredEnrolledUsers}
            rowKey="_id"
            pagination={{ pageSize: 5 }}
          />
        </Form>
      </Modal>
    </div>
  );
};

export default Fund;
